import {
	AppBase,
	BoundingBox,
	createMesh,
	Entity,
	Mat4,
	Material,
	MeshInstance,
	RenderComponent,
	Vec3,
} from "playcanvas";
import { ringWidth, roomSizing, shadowHeight } from "./room-constants";
import {
	createRoomGeometry,
	layoutFromLocalBounds,
	layoutsAreEqual,
	MeshData,
	RoomLayout,
} from "./room-geometry";
import {
	loadFloorStyle,
	loadWallMaterials,
	RoomMaterialPair,
	setPairDiffuse,
	wallColorValue,
} from "./room-materials";
import {
	defaultFloorStyle,
	defaultWallColor,
	FloorStyleName,
	WallColorName,
} from "./room-names";

export type RoomOptions = {
	requestFrame(): void;
	wallColor?: WallColorName;
	floorStyle?: FloorStyleName;
};

type RoomMaterials = {
	floor: RoomMaterialPair;
	wall: RoomMaterialPair;
};

/**
 * A floor and a wall generated from the configuration's bounds, rebuilt on
 * `prerender` whenever the size changes.
 */
export class Room {
	public readonly entity: Entity;
	/** Resolves when the materials and their textures are ready to render. */
	public readonly ready: Promise<void>;

	private _app: AppBase;
	private _requestFrame: () => void;
	private _render: RenderComponent;
	private _wall?: RoomMaterialPair;
	private _floor?: RoomMaterialPair;
	private _floorStyle: FloorStyleName;
	private _floorRequest = 0;
	private _boundsMin = new Vec3();
	private _boundsMax = new Vec3();
	private _layout?: RoomLayout;
	private _wallColor: WallColorName;
	private _destroyed = false;
	private _worldToLocal = new Mat4();
	private _localMin = new Vec3();
	private _localMax = new Vec3();

	constructor(app: AppBase, entity: Entity, options: RoomOptions) {
		this._app = app;
		this.entity = entity;
		this._requestFrame = options.requestFrame;
		this._wallColor = options.wallColor ?? defaultWallColor;
		this._floorStyle = options.floorStyle ?? defaultFloorStyle;
		this._render = entity.addComponent("render", {
			type: "asset",
			castShadows: true,
			receiveShadows: true,
		}) as RenderComponent;

		const wall = loadWallMaterials(app).then((pair) => {
			if (this._destroyed) {
				return;
			}
			setPairDiffuse(pair, wallColorValue(this._wallColor));
			this._wall = pair;
			this._layout = undefined;
			this._requestFrame();
		});
		this.ready = Promise.all([wall, this._loadFloor(this._floorStyle)]).then(
			() => undefined,
		);

		app.on("prerender", this._onPrerender, this);
	}

	public get wallColor(): WallColorName {
		return this._wallColor;
	}

	public set wallColor(name: WallColorName) {
		this._wallColor = name;
		if (this._wall && setPairDiffuse(this._wall, wallColorValue(name))) {
			this._requestFrame();
		}
	}

	public get floorStyle(): FloorStyleName {
		return this._floorStyle;
	}

	/** Swaps the floor's material once its textures have loaded. */
	public set floorStyle(name: FloorStyleName) {
		if (name === this._floorStyle) {
			return;
		}
		this._floorStyle = name;
		void this._loadFloor(name);
	}

	/** Requests a frame only when the bounds change; it is called every tick. */
	public updateBoundsFromBoundingBox(aabb: BoundingBox): void {
		const min = aabb.getMin();
		const max = aabb.getMax();
		if (this._boundsMin.equals(min) && this._boundsMax.equals(max)) {
			return;
		}
		this._boundsMin.copy(min);
		this._boundsMax.copy(max);
		this._requestFrame();
	}

	public destroy(): void {
		this._destroyed = true;
		this._app.off("prerender", this._onPrerender, this);
		if (this.entity.render === this._render) {
			this.entity.removeComponent("render");
		}
	}

	/**
	 * Loads a style and makes it the floor on the next rebuild, unless a later
	 * style was asked for while it loaded or the room was destroyed meanwhile.
	 */
	private async _loadFloor(name: FloorStyleName): Promise<void> {
		const request = ++this._floorRequest;
		const pair = await loadFloorStyle(this._app, name);
		if (this._destroyed || request !== this._floorRequest) {
			return;
		}
		this._floor = pair;
		this._layout = undefined;
		this._requestFrame();
	}

	private _onPrerender(): void {
		const { _wall: wall, _floor: floor } = this;
		if (!wall || !floor) {
			return;
		}
		const materials: RoomMaterials = { wall, floor };
		const layout = this._computeLayout();
		if (this._layout && layoutsAreEqual(this._layout, layout)) {
			return;
		}
		this._layout = layout;
		this._rebuild(layout, materials);
	}

	private _computeLayout(): RoomLayout {
		const {
			_worldToLocal: worldToLocal,
			_localMin: min,
			_localMax: max,
		} = this;
		worldToLocal.copy(this.entity.getWorldTransform()).invert();
		worldToLocal.transformPoint(this._boundsMin, min);
		worldToLocal.transformPoint(this._boundsMax, max);
		return layoutFromLocalBounds(min, max, roomSizing);
	}

	private _rebuild(layout: RoomLayout, materials: RoomMaterials): void {
		const geometry = createRoomGeometry({ ...layout, ringWidth, shadowHeight });
		const instance = (data: MeshData, material: Material) => {
			const mesh = createMesh(this._app.graphicsDevice, data.positions, {
				normals: data.normals,
				uvs: data.uvs,
				uvs1: data.uvs1,
				colors: data.colors.map((c) => Math.round(c * 255)),
				indices: data.indices,
			});
			return new MeshInstance(mesh, material, this.entity);
		};
		for (const previous of this._render.meshInstances) {
			previous.mesh.destroy();
		}
		this._render.meshInstances = [
			instance(geometry.floorCore, materials.floor.core),
			instance(geometry.floorRing, materials.floor.ring),
			instance(geometry.wallCore, materials.wall.core),
			instance(geometry.wallRing, materials.wall.ring),
		];
	}
}
